import { handlePaginatedData } from '../services/DataHelperFunctions';
import { EAction, EApiType, EUrlParams } from '../data/Constants';
import AxiosClient from './AxiosClient';
import { handleSuccess, handleApiErrorWithFallback, handleError } from './HttpUtil';
import type { Dispatch } from '@reduxjs/toolkit';

import type {
  IDeleteRequest,
  IPaginationRequest,
  IPropertiesRequest,
  IPropertyAssociationRequest,
  ISearchRequest,
  IUpdateRequest,
} from '../../types/IApiTypes';

export const PropertyApi = {
  //Get all the properties from the db
  getProperties: (action: IPropertiesRequest, dispatch: Dispatch) => {
    let queryParams = new URLSearchParams();
    if (action.page && action.limit) {
      queryParams = new URLSearchParams({
        [EUrlParams.Page]: String(action.page),
        [EUrlParams.Limit]: String(action.limit),
      });
    }

    if (action.searchCode) {
      queryParams.set(EUrlParams.Search, action.searchCode);
    }

    return AxiosClient.post('/property/v1/read' + (queryParams ? `?${queryParams}` : ''), {})
      .then((response) => {
        return handlePaginatedData(response, action);
      })
      .catch((err) => {
        return handleApiErrorWithFallback(
          dispatch,
          err,
          { page: action.page, searchKey: action.searchCode },
          EAction.Read,
          EApiType.PropertyKey
        );
      });
  },

  //Get only one property based on the search value
  getPropertyDetails: (action: ISearchRequest, dispatch: Dispatch) => {
    const requestBody = action.searchCode ? { filters: { code: action.searchCode } } : {};
    return AxiosClient.post('/property/v1/read', requestBody)
      .then((response) => {
        return handleSuccess(response);
      })
      .catch((err) => {
        return handleError(dispatch, err, EAction.Get);
      });
  },

  //This Api will Delete the property from the db
  deleteProperty: (action: IDeleteRequest, dispatch: Dispatch) => {
    return AxiosClient.post('/property/v1/delete', action.request)
      .then((response) => {
        return handleSuccess(response);
      })
      .catch((err) => {
        return handleError(dispatch, err);
      });
  },

  //update the existing properties in the db
  updateProperty: (action: IUpdateRequest, dispatch: Dispatch) => {
    return AxiosClient.post('/property/v1/update', action.request)
      .then((response) => {
        return handleSuccess(response);
      })
      .catch((err) => {
        return handleError(dispatch, err, EAction.Update);
      });
  },

  //THis Api will create the new property
  createProperty: (action: IUpdateRequest, dispatch: Dispatch) => {
    return AxiosClient.post('/property/v1/create', action.request)
      .then((response) => {
        return handleSuccess(response);
      })
      .catch((err) => {
        return handleError(dispatch, err, EAction.Create);
      });
  },

  //Get all the properties from the db
  getFeaturePropertyAssociations: (action: IPropertyAssociationRequest, dispatch: Dispatch) => {
    action.page = action.searchCode ? 1 : action.page;
    action.limit = action.searchCode ? 0 : action.limit;
    const pageInfo = `${EUrlParams.Page}=` + action.page + `&${EUrlParams.Limit}=` + action.limit;

    const req = {
      includeFeature: true,
      productCode: action.productId,
      searchCode: action.searchCode ? action.searchCode : null,
    };

    return AxiosClient.post(`/property/v1/association/read?${pageInfo}`, req)
      .then((response) => {
        const total =
          req.searchCode == null ? JSON.parse(response.headers['x-pagination']).total : 1;
        return {
          data: response.data,
          searchKey: action.searchCode,
          page: req.searchCode == null ? action.page : 1,
          totalRecords: total,
          recordsPerPage: action.limit,
        };
      })
      .catch((err) => {
        return handleError(dispatch, err, action);
      });
  },

  //Get all the properties from the db
  getPropertyAssociations: (action: IPaginationRequest, dispatch: Dispatch) => {
    const queryParams = new URLSearchParams({
      [EUrlParams.Page]: String(action.page),
      [EUrlParams.Limit]: String(action.limit),
    });
    if (action.searchCode) {
      queryParams.set(EUrlParams.ProductSearch, action.searchCode);
    }

    return AxiosClient.post(`/property/v1/productmodel/association/read?${queryParams}`, {})
      .then((response) => {
        return handlePaginatedData(response, action);
      })
      .catch((err) => {
        return handleApiErrorWithFallback(
          dispatch,
          err,
          { page: action.page, searchKey: action.searchCode },
          EAction.Read,
          EApiType.PropertyAssociationKey
        );
      });
  },

  //This method returns the List Of Enum Values
  getEnumListOfValues: (dispatch: Dispatch) => {
    return AxiosClient.get('/lov/v1/details')
      .then((response) => {
        return handleSuccess(response);
      })
      .catch((err) => {
        return handleError(dispatch, err);
      });
  },
};
